import React from 'react';
import { classes, st } from './CornerRadiusInput.st.css';
import { CornerRadiusInputProps } from './CornerRadiusInput.types';
import { POSITION, dataHooks } from './CornerRadiusInput.constants';

import LiveRegion from '../LiveRegion';
import { InputField } from './components/InputField/InputField';
import { useLinkedMode } from './hooks/useLinkedMode';
import { useBroadcast } from './hooks/useBroadcast';
import { LinkingButton } from './components/LinkingButton/LinkingButton';

/** input component used for entering corner radius value for each corner of an element */
const CornerRadiusInput = ({
  dataHook,
  className,
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
  size = 'medium',
  linked = false,
  onLinkedToggle,
  linkingButtonLabels,
  linkedValuesChangedMessage,
  min = 0,
  max = 999,
}: CornerRadiusInputProps) => {
  const [liveRegionRef, broadcast] = useBroadcast();

  const [linkedProperties, setLinking] = useLinkedMode({
    initial: linked,
    onChangeCallbacks: [
      topLeft?.onChange,
      topRight?.onChange,
      bottomLeft?.onChange,
      bottomRight?.onChange,
    ],
    onInvalidCallbacks: [
      topLeft?.onInvalid,
      topRight?.onInvalid,
      bottomLeft?.onInvalid,
      bottomRight?.onInvalid,
    ],
    onBlurCallbacks: [() => broadcast(linkedValuesChangedMessage)],
  });

  const commonInputProps = {
    size,
    min,
    max,
    linkedProperties,
  };

  return (
    <div data-hook={dataHook} className={st(classes.root, className)}>
      <LiveRegion ref={liveRegionRef} dataHook={dataHooks.liveRegion} />

      <div className={classes.linkingButton}>
        <LinkingButton
          size={size}
          isLinkingEnabled={linkedProperties.isLinkingEnabled}
          setLinking={setLinking}
          linkingButtonLabels={linkingButtonLabels}
          onLinkedToggle={onLinkedToggle}
        />
      </div>

      <div className={classes.topLeft}>
        <InputField
          position={POSITION.topLeft}
          {...commonInputProps}
          {...topLeft}
          {...linkedProperties}
        />
      </div>

      <div className={classes.topRight}>
        <InputField
          position={POSITION.topRight}
          {...commonInputProps}
          {...topRight}
          {...linkedProperties}
        />
      </div>

      <div className={classes.bottomLeft}>
        <InputField
          position={POSITION.bottomLeft}
          {...commonInputProps}
          {...bottomLeft}
          {...linkedProperties}
        />
      </div>

      <div className={classes.bottomRight}>
        <InputField
          position={POSITION.bottomRight}
          {...commonInputProps}
          {...bottomRight}
          {...linkedProperties}
        />
      </div>
    </div>
  );
};

CornerRadiusInput.displayName = 'CornerRadiusInput';

// Proptypes are not required, because they're only used for storybook documentation
export default CornerRadiusInput;
